import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  persistStore,
  createMigrate,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { api } from 'services/api';
import authSlice from './auth/auth-slice';
import cacheSlice from './cache/cache-slice';
import appSlice from './app/app-slice';
import appointmentSlice from './appointment/appointment-slice';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import type { PersistConfig } from 'redux-persist/lib/types';
import { migrations } from 'utils/migrations';
import subscriptionPurchaseSlice from './subscriptionPurchase/subscription-purchase-slice';
import membershipProgramsSlice from './membershipPrograms/membership-programs-slice';

const persistConfig: PersistConfig<ReturnType<typeof rootReducer>> = {
  key: 'root',
  storage,
  blacklist: ['api'],
  version: 2,
  stateReconciler: autoMergeLevel2,
  migrate: createMigrate(migrations, { debug: true }),
};

const reducers = {
  [api.reducerPath]: api.reducer,
  auth: authSlice.reducer,
  cache: cacheSlice.reducer,
  app: appSlice.reducer,
  appointment: appointmentSlice.reducer,
  subscriptionPurchase: subscriptionPurchaseSlice.reducer,
  membershipPrograms: membershipProgramsSlice.reducer,
};

const rootReducer = combineReducers(reducers);

// @ts-expect-error must be the same as state type reducers
const proxyReducer = (state, action) => {
  if (action.type === 'auth/logout') {
    const { app, ...restReducers } = state as typeof reducers;
    state = { app };
  }
  if (action.type === 'resetApp') {
    state = undefined;
    localStorage.removeItem('persist:root');
  }

  return rootReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, proxyReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  // Adding the api middleware enables caching, invalidation, polling,
  // and other useful features of `rtk-query`.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(api.middleware),
});

export const persistor = persistStore(store);

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

